import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Zap, Play, Square, Mic, RotateCcw } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { FormData, occasionOptions, recipientOptions, feelingOptions, songStyleOptions, tempoOptions, languageOptions } from "../types";

interface ReviewStepProps {
  formData: FormData;
  onRushChange: (value: boolean) => void;
  onRecordingChange?: (blob: Blob | null, url: string) => void;
}

const ReviewStep = ({ formData, onRushChange, onRecordingChange }: ReviewStepProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const getLabel = (options: { value: string; label: string }[], value: string, otherValue?: string) => {
    if (value === "other" && otherValue) return otherValue;
    const option = options.find(o => o.value === value);
    return option?.label || value;
  };

  const playRecording = () => {
    if (formData.pronunciationRecordingUrl && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const audioUrl = URL.createObjectURL(audioBlob);
        onRecordingChange?.(audioBlob, audioUrl);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const basePrice = formData.planType === "premium" ? 85 : 35;
  const rushPrice = formData.rushDelivery ? 10 : 0;
  const totalPrice = basePrice + rushPrice;

  return (
    <motion.div
      key="review"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Review Your Song
        </h2>
        <p className="text-muted-foreground">
          Make sure everything looks perfect before completing your order.
        </p>
      </div>

      <div className="space-y-4 bg-muted/50 rounded-xl p-6">
        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Package</span>
          <span className="font-medium capitalize">
            {formData.planType === "premium" ? "Premium Song + Slideshow" : "Essentials Plan"}
          </span>
        </div>
        
        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Occasion</span>
          <span className="font-medium">
            {getLabel(occasionOptions, formData.occasion, formData.occasionOther)}
          </span>
        </div>

        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Recipient</span>
          <span className="font-medium">
            {getLabel(recipientOptions, formData.recipient, formData.recipientOther)}
          </span>
        </div>

        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Names</span>
          <span className="font-medium">{formData.names}</span>
        </div>

        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Feeling</span>
          <span className="font-medium">
            {getLabel(feelingOptions, formData.feeling, formData.feelingOther)}
          </span>
        </div>

        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Style</span>
          <span className="font-medium">
            {getLabel(songStyleOptions, formData.songStyle, formData.songStyleOther)}
          </span>
        </div>

        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Tempo</span>
          <span className="font-medium">
            {getLabel(tempoOptions, formData.tempo, formData.tempoOther)}
          </span>
        </div>

        <div className="flex justify-between py-2 border-b border-border">
          <span className="text-muted-foreground">Language</span>
          <span className="font-medium">
            {getLabel(languageOptions, formData.language)}
          </span>
        </div>

        {formData.singerGender && (
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-muted-foreground">Singer Gender</span>
            <span className="font-medium">
              {({ male: "Male", female: "Female", male_and_female: "Male and Female", surprise_me: "Surprise Me" } as Record<string, string>)[formData.singerGender] || formData.singerGender}
            </span>
          </div>
        )}

        {formData.story && (
          <div className="py-2 border-b border-border">
            <span className="text-muted-foreground text-sm">Story & Notes</span>
            <p className="font-medium text-sm mt-1 whitespace-pre-wrap">{formData.story}</p>
          </div>
        )}

        {formData.planType === "premium" && (
          <>
            {formData.includeDateInVideo === "yes" && formData.videoDate && (
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Video Date</span>
                <span className="font-medium">
                  {new Date(formData.videoDate).toLocaleDateString()}
                </span>
              </div>
            )}
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground">Photos</span>
              <span className="font-medium">
                {formData.uploadPhotosNow === "later" 
                  ? "Will upload later" 
                  : `${formData.uploadedImages?.length || 0} photos uploaded`}
              </span>
            </div>
          </>
        )}

        <div className="py-2 border-b border-border">
          <span className="text-muted-foreground">Pronunciation</span>
          {formData.pronunciationRecordingUrl ? (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <audio
                ref={audioRef}
                src={formData.pronunciationRecordingUrl}
                onEnded={() => setIsPlaying(false)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={isPlaying ? stopPlayback : playRecording}
              >
                {isPlaying ? <Square className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                {isPlaying ? "Stop" : "Play"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? (
                  <><Square className="w-3 h-3 mr-1" /> Stop</>
                ) : (
                  <><RotateCcw className="w-3 h-3 mr-1" /> Re-record</>
                )}
              </Button>
              {isRecording && (
                <span className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
              )}
              <span className="text-sm text-primary font-medium ml-auto">✓ Recorded</span>
            </div>
          ) : (
            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? (
                  <><Square className="w-3 h-3 mr-1" /> Stop Recording</>
                ) : (
                  <><Mic className="w-3 h-3 mr-1" /> Record Pronunciation</>
                )}
              </Button>
              {isRecording && (
                <span className="ml-2 text-sm text-muted-foreground">
                  <span className="inline-block w-2 h-2 bg-destructive rounded-full animate-pulse mr-1" />
                  Recording...
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rush Delivery Option */}
      <div className="bg-accent/20 border border-accent rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-accent-foreground" />
            <div>
              <Label htmlFor="rush" className="text-base font-medium cursor-pointer">
                ⚡ Rush Delivery (+$10)
              </Label>
              <p className="text-sm text-muted-foreground">
                Guaranteed delivery within 24 hours
              </p>
            </div>
          </div>
          <Switch
            id="rush"
            checked={formData.rushDelivery}
            onCheckedChange={onRushChange}
          />
        </div>
      </div>

      {/* Price Summary */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h4 className="font-medium text-foreground mb-4">Order Summary</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {formData.planType === "premium" ? "Premium Song + Slideshow" : "Essentials Plan"}
            </span>
            <span>${basePrice}</span>
          </div>
          {formData.rushDelivery && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rush Delivery</span>
              <span>+$10</span>
            </div>
          )}
          <div className="border-t border-border pt-2 mt-2">
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>${totalPrice}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReviewStep;
